/** Fields where the stored contract record disagrees with the document.
 *
 *  Produced by the gate (INV-MATCH-01). Each row is a field the agents read one
 *  way out of the contract and the contract record holds another way — usually
 *  because a reviewer edited an agent's output or the metadata directly. Neither
 *  side is assumed correct; the reviewer decides which is, which is why both are
 *  shown side by side rather than one being presented as a correction.
 */
import { Card, CardContent } from '@/components/ui/card';
import { GitCompareArrows } from 'lucide-react';

export interface ConsistencyMismatch {
  field?: string;
  label?: string;
  extracted?: string;
  recorded?: string;
  description?: string;
}

export function ConsistencyMismatchPanel({
  mismatches,
}: {
  mismatches: ConsistencyMismatch[] | undefined;
}) {
  if (!mismatches || mismatches.length === 0) return null;

  return (
    <Card className="border-amber-500/40">
      <CardContent className="space-y-3 px-4 pt-3 pb-3 text-xs">
        <div className="flex items-center gap-2">
          <GitCompareArrows className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          <span className="text-sm font-medium">
            Document and contract record disagree ({mismatches.length})
          </span>
        </div>

        <ul className="space-y-2" aria-label="Field mismatches">
          {mismatches.map((m, i) => (
            <li key={`${m.field ?? ''}-${i}`} className="rounded border p-2.5 space-y-1.5">
              <p className="font-medium">{m.label || m.field}</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="text-muted-foreground">Document says</dt>
                <dd>{m.extracted || '—'}</dd>
                <dt className="text-muted-foreground">Record holds</dt>
                <dd>{m.recorded || '—'}</dd>
              </dl>
            </li>
          ))}
        </ul>

        <p className="border-t pt-2 text-[10px] text-muted-foreground">
          A record value that differs from the document is usually a reviewer edit, not an error.
          Confirm which is correct before approving — approval requires a senior reviewer while
          any of these stand.
        </p>
      </CardContent>
    </Card>
  );
}
